//  Worker timeout is 10 minutes, to allow for long-running requests
export const WORKER_TIMEOUT_IN_SECONDS = 60 * 10;

export const METRIC_WORKER_NAMESPACE = 'SparkPlug/Worker';
export const METRIC_API_NAMESPACE = 'SparkPlug/API';

export type CloudEventType =
  | 'account'
  | 'tagAssistant'
  | 's3Delete'
  | 'toastPartner'
  | 'shopifyGdpr';
export type CloudEventDetailedType = `${CloudEventType}:${string}`;
export type WorkerJobSubtype = 'default' | 'cache' | 'import' | 'replay';
export type WorkerEventDetailType = `worker:${WorkerJobSubtype}:${JobType}`;
export type EventDetailType = WorkerEventDetailType | CloudEventType | CloudEventDetailedType;

export const SCHEDULED_JOBS = [
  'cacheActiveSparks',
  'dailyFailedBillingInvoicesReport',
  'dataDelete',
  'migrations',
  'netNewProductsMetric',
  'nextGenPayoutsReports',
  'notificationCleanup',
  'pendingPayoutsReport',
  'pointOfSaleLocations',
  'pointOfSaleTransactions',
  'pointOfSaleStatus',
  'posMonitor',
  'reconcilePointOfSale',
  'recurringSparks',
  'rewardNotifications',
  'sendPendingBrandLinkReminders',
  'sendPendingSparkReminders',
  'sparkExpiration',
  'sparkNotifications',
  'syncUnlinkedVendorRetailers',
  'totalActiveBrandLinksMetric',
  'updateAllSparkDatapoints',
  'updateAllAccountMetrics',
  'updateAccountStatuses',
  'updateSparkCounts',
  'weeklyDigestEmails',
] as const;
export type ScheduledJob = typeof SCHEDULED_JOBS[number];

export const UNSCHEDULED_JOBS = [
  'accountSparkReportEmail',
  'alleaves',
  'alleavesTransactions',
  'backfillSparkPosArchive',
  'biotrack',
  'biotrackTransactions',
  'blaze',
  'blazeTransactions',
  'blockSmsForUser',
  'cacheAccountUsersAndProfiles',
  'clover',
  'cloverTransactions',
  'createSparkPosArchive',
  'cova',
  'covaTransactions',
  'employeeEarningsExportEmail',
  'flowhub',
  'flowhubTransactions',
  'flourish',
  'flourishTransactions',
  'greenbits',
  'greenbitsTransactions',
  'growflow',
  'growflowTransactions',
  'iheartjane',
  'iheartjaneTransactions',
  'indicaonline',
  'indicaonlineTransactions',
  'leaflogix',
  'leaflogixTransactions',
  'lightspeed',
  'lightspeedTransactions',
  'meadow',
  'meadowTransactions',
  'mjfreeway',
  'mjfreewayTransactions',
  'revel',
  'revelTransactions',
  'persistIntercomCompanyId',
  'portal42',
  'portal42Transactions',
  'posabit',
  'posabitTransactions',
  'shopify',
  'shopifyTransactions',
  'sparkNotificationsAccount',
  'square',
  'squareTransactions',
  'summarizeTransactions',
  'sweed',
  'sweedTransactions',
  'sweedInventoryWebhook',
  'sweedLocationWebhook',
  'sweedTransactionWebhook',
  'tagAssistant:applyPrediction',
  'tagAssistant:markProductAsInvalid',
  'tagAssistant:predictProductTagsForNewProduct',
  'tagAssistant:predictProductTagsForBrand',
  'tagAssistant:predictProductTagsForVendor',
  'toast',
  'toastTransactions',
  'treez',
  'treezTransactions',
  'treezWebhook',
  'updateAccountBrandLinkMetrics',
  'updateAccountSparkBalanceMetric',
  'updateAccountTagCountMetric',
  'updateAccountUserMetrics',
  'updateAccountZeroSparkDaysMetric',
  'updateSparkDatapoints',
  'userLastTransaction',
  'vend',
  'vendTransactions',
] as const;
export type UnscheduledJob = typeof UNSCHEDULED_JOBS[number];

export const JOB_TYPES = [...SCHEDULED_JOBS, ...UNSCHEDULED_JOBS] as const;

export type JobType = typeof JOB_TYPES[number];

interface RecursiveS3ActionPayloadBase {
  Bucket: string;
}

interface RecursiveS3ActionPrefixPayload extends RecursiveS3ActionPayloadBase {
  Prefix: string;
  ContinuationToken?: string;
}

interface RecursiveS3ActionObjectPayload extends RecursiveS3ActionPayloadBase {
  Key: string;
}

export type RecursiveS3ActionPayload =
  | RecursiveS3ActionObjectPayload
  | RecursiveS3ActionPrefixPayload;

export function isRecursiveS3ActionPrefixPayload(
  payload: RecursiveS3ActionPayload,
): payload is RecursiveS3ActionPrefixPayload {
  return 'Prefix' in payload;
}
